import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { Heading, Link, Paragraph } from '@digdir/designsystemet-react';
import type { HrWorkEnvironmentOverview } from '../../../../api/hrWorkEnvironment';
import { Routes } from '../../../../enums/Routes';
import {
  percent,
  processPhases,
  riskShares,
  teamsInProcess,
  topRisks,
} from './hrOverviewView';
import { RiskBar } from './RiskBar';
import classes from './Overview.module.css';

/** Where the teams are in the process, and the risks that recur, with links to the details */
export const WorkInTeams = ({
  overview,
}: {
  overview: HrWorkEnvironmentOverview;
}): ReactElement => {
  const { t } = useTranslation();
  const risks = topRisks(overview);
  return (
    <section aria-labelledby="work-heading" className={classes.section}>
      <Heading level={2} data-size="md" id="work-heading">
        {t('hrOverview.work.title')}
      </Heading>
      <div className={classes.pair}>
        <section aria-labelledby="process-heading" className={classes.card}>
          <div className={classes.heading}>
            <Heading level={3} data-size="xs" id="process-heading">
              {t('hrOverview.work.process.title', {
                count: teamsInProcess(overview.teams).length,
              })}
            </Heading>
            <Paragraph data-size="sm" className={classes.subtle}>
              {t('hrOverview.work.process.intro')}
            </Paragraph>
          </div>
          <ol className={classes.bars}>
            {processPhases(overview.teams).map(({ phase, count, share }) => (
              <li key={phase}>
                <span className="ds-paragraph" data-size="sm">
                  {t(`hrOverview.phases.${phase}.title`)}
                </span>
                <span className="ds-label" data-size="sm">
                  {t('hrOverview.teamCount', { count })}
                </span>
                <span className={classes.track} aria-hidden="true">
                  <span
                    className={classes.fill}
                    style={{ width: percent(share) }}
                  />
                </span>
              </li>
            ))}
          </ol>
          <Link asChild data-size="sm" className={classes.start}>
            <RouterLink to={Routes.HRWorkEnvironmentProcessFullPath}>
              {t('hrOverview.work.process.link')}
            </RouterLink>
          </Link>
        </section>
        <section aria-labelledby="risk-heading" className={classes.card}>
          <div className={classes.heading}>
            <Heading level={3} data-size="xs" id="risk-heading">
              {t('hrOverview.work.risks.title')}
            </Heading>
            <Paragraph data-size="sm" className={classes.subtle}>
              {t('hrOverview.work.risks.intro', {
                count: overview.teamsWithResults,
              })}
            </Paragraph>
          </div>
          {risks.length === 0 ? (
            <Paragraph data-size="sm">
              {t('hrOverview.work.risks.none')}
            </Paragraph>
          ) : (
            <ol className={classes.bars}>
              {risks.map((risk) => (
                <li key={risk.key}>
                  <span className="ds-paragraph" data-size="sm">
                    {risk.name}
                  </span>
                  <span className="ds-label" data-size="sm">
                    {t('hrOverview.work.risks.count', {
                      count: risk.high + risk.moderate,
                      total: overview.teamsWithResults,
                    })}
                  </span>
                  <RiskBar shares={riskShares(risk, overview)} />
                </li>
              ))}
            </ol>
          )}
          <Link asChild data-size="sm" className={classes.start}>
            <RouterLink to={Routes.HRWorkEnvironmentRisksFullPath}>
              {t('hrOverview.work.risks.link')}
            </RouterLink>
          </Link>
        </section>
      </div>
    </section>
  );
};
